import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import ChatBot from '../components/ChatBot';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../components/ui/accordion';
import { Play, ArrowRight, CheckCircle2, Award, Scale, Megaphone } from 'lucide-react';
import { servicesAPI } from '../api';
import { useNavigate } from 'react-router-dom';
import SEO from '../components/SEO';
import StructuredData from '../components/StructuredData';
import { faqSections, buildFaqSchema } from '../data/serviceFaqs';
import { articles } from '../data/articles';

const SITE_URL = (process.env.REACT_APP_SITE_URL || 'https://consultiumgroup.co.za').replace(/\/$/, '');

const localBusinessSchema = {
  '@context': 'https://schema.org',
  '@type': 'LocalBusiness',
  '@id': `${SITE_URL}/#organization`,
  name: 'Consultium Group',
  legalName: 'Phatsimo Enterprises T/A Consultium Advisory',
  url: SITE_URL,
  logo: `${SITE_URL}/logo192.png`,
  description:
    'South African business services firm specialising in CIPC company registrations, SARS tax clearance, B-BBEE, ISO certification and growth strategy.',
  telephone: ['+27128243708', '+27790424911', '+27782850927', '+27500578315'],
  email: 'hello@consultiumgroup.co.za',
  address: { '@type': 'PostalAddress', addressCountry: 'ZA' },
  openingHoursSpecification: [
    {
      '@type': 'OpeningHoursSpecification',
      dayOfWeek: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'],
      opens: '09:00',
      closes: '17:00',
    },
  ],
  priceRange: 'R390 – R25000',
  areaServed: { '@type': 'Country', name: 'South Africa' },
  sameAs: [],
};

const homeFaqItems = [
  ...(faqSections.find((s) => s.id === 'company-registration')?.items || []).slice(0, 3),
  ...(faqSections.find((s) => s.id === 'tax-compliance')?.items || []).filter((it) =>
    ['What is a Public Officer?', 'What is tax clearance or Tax Compliance Status?'].includes(it.q)
  ),
];

const faqSchema = buildFaqSchema(homeFaqItems);

const Home = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('popular');
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const { data } = await servicesAPI.getAll();
        setServices(data);
      } catch (error) {
        console.error('Error fetching data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  const popularServices = services.filter(s => s.popular);
  const registrationServices = services.filter(s => s.category === 'registrations');
  const taxServices = services.filter(s => s.category === 'tax');
  const otherServices = services.filter(s => s.category === 'other' || s.category === 'amendments');
  
  if (loading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  const ServiceCard = ({ service }) => (
    <Card className="group hover:shadow-xl transition-all duration-300 hover:-translate-y-1">
      <CardHeader className="p-0">
        <div className="relative overflow-hidden rounded-t-lg h-48">
          <img 
            src={service.image} 
            alt={service.name}
            loading="lazy"
            decoding="async"
            className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-300"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
          <div className="absolute bottom-4 left-4 right-4">
            <h3 className="text-white font-bold text-lg">{service.name}</h3>
          </div>
        </div>
      </CardHeader>
      <CardContent className="pt-4">
        <div className="flex justify-between items-center mb-3">
          <p className="text-2xl font-bold text-emerald-600">
            {service.enquiryOnly ? (
              <span className="text-lg">Price on Enquiry</span>
            ) : (
              <>
                R{service.price}
                {service.additionalFee && <span className="text-sm text-gray-500"> + {service.additionalFee}</span>}
              </>
            )}
          </p>
          <p className="text-sm text-gray-600">({service.duration})</p>
        </div>
        <p className="text-gray-600 text-sm mb-4">{service.description}</p>
      </CardContent>
      <CardFooter className="flex gap-2">
        <Button 
          onClick={() => navigate(`/service/${service.id}`)}
          className="flex-1 bg-blue-600 hover:bg-blue-700"
        >
          Start
        </Button>
        <Button variant="outline" className="px-3">
          <Play className="h-4 w-4" />
        </Button>
      </CardFooter>
    </Card>
  );

  const PackageCard = ({ pkg }) => (
    <Card className="hover:shadow-xl transition-all duration-300 hover:-translate-y-1 border-2 border-red-100">
      <CardHeader className="text-center bg-gradient-to-br from-emerald-50 to-white">
        <div className="w-16 h-16 bg-emerald-600 text-white rounded-full flex items-center justify-center text-2xl font-bold mx-auto mb-3">
          {pkg.icon}
        </div>
        <CardTitle className="text-2xl text-gray-800">{pkg.name}</CardTitle>
        <CardDescription className="text-3xl font-bold text-emerald-600 mt-2">
          R{pkg.price}
        </CardDescription>
      </CardHeader>
      <CardContent className="pt-6">
        <ul className="space-y-2">
          {pkg.features.map((feature) => (
            <li key={feature} className="flex items-start gap-2 text-sm">
              <CheckCircle2 className="h-4 w-4 text-green-600 mt-0.5 flex-shrink-0" />
              <span>{feature}</span>
            </li>
          ))}
        </ul>
      </CardContent>
      <CardFooter>
        <Button className="w-full bg-emerald-600 hover:bg-emerald-700">
          Buy Now
        </Button>
      </CardFooter>
    </Card>
  );

  return (
    <div className="min-h-screen bg-white">
      <SEO
        title="Register a Company in South Africa from R590 | Consultium Group"
        description="Register your Pty Ltd from R590 in 24–48 hours. CIPC company registration, tax clearance, B-BBEE, annual returns, ISO & compliance. Trusted South African business services since 2000."
        path="/"
      />
      <StructuredData data={localBusinessSchema} />
      <StructuredData data={faqSchema} />
      <Navbar />
      
      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-black via-emerald-950 to-black overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          <div className="grid md:grid-cols-2 gap-8 items-center">
            <div className="z-10">
              <Button variant="outline" className="mb-4 bg-emerald-600 text-white border-emerald-600 hover:bg-emerald-700">
                Login
              </Button>
              <div className="mb-6">
                <p className="text-lg mb-2" style={{ fontFamily: "'Rethink Sans', 'Inter', system-ui, sans-serif", letterSpacing: '-0.02em' }}>
                  <span className="text-emerald-500 font-bold text-3xl md:text-4xl">Consultium Group</span>
                </p>
                <div className="flex items-center gap-2 text-sm text-gray-400">
                  <div className="px-3 py-1 bg-emerald-900 border-2 border-emerald-600 rounded-full text-emerald-300">
                    EST. 2000
                  </div>
                </div>
              </div>
              <h1 className="text-4xl md:text-5xl font-bold text-white mb-4 leading-tight">
                Register a Company in South Africa
              </h1>
              <h2 className="text-xl md:text-2xl text-emerald-200 mb-6 font-medium">
                Fast CIPC registration · Tax clearance · B-BBEE · ISO — all under one roof
              </h2>
              <div className="mb-6">
                <p className="text-lg text-gray-400 mb-2">New Pty Ltd from</p>
                <p className="text-5xl font-bold text-emerald-500">R590</p>
                <p className="text-gray-400">Delivered in 24–48 hours*</p>
              </div>
            </div>
            <div className="relative">
              <div className="absolute inset-0 bg-gradient-to-br from-emerald-600/20 to-black/40 rounded-lg z-10"></div>
              <img 
                src="https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?w=800&h=900&fit=crop&q=70&auto=format"
                alt="Modern business skyline representing Consultium Group's professional CIPC company registration and business compliance services in South Africa"
                loading="eager"
                decoding="async"
                fetchPriority="high"
                width="800"
                height="900"
                className="rounded-lg shadow-2xl w-full h-auto"
              />
            </div>
          </div>
        </div>
        
        {/* Curved Bottom */}
        <div className="absolute bottom-0 left-0 right-0">
          <svg viewBox="0 0 1440 120" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M0 120L1440 120L1440 0C1440 0 1080 80 720 80C360 80 0 0 0 0L0 120Z" fill="#059669"/>
          </svg>
        </div>
      </section>

      {/* Tabbed Services Section */}
      <section id="popular" className="bg-emerald-600 py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
            <TabsList className="grid w-full grid-cols-4 bg-white/10 backdrop-blur-sm h-auto p-1">
              <TabsTrigger value="popular" className="data-[state=active]:bg-white data-[state=active]:text-emerald-600 text-white font-semibold py-3">
                Popular
              </TabsTrigger>
              <TabsTrigger value="registrations" className="data-[state=active]:bg-white data-[state=active]:text-emerald-600 text-white font-semibold py-3">
                Registrations
              </TabsTrigger>
              <TabsTrigger value="tax" className="data-[state=active]:bg-white data-[state=active]:text-emerald-600 text-white font-semibold py-3">
                Tax and SARS
              </TabsTrigger>
              <TabsTrigger value="other" className="data-[state=active]:bg-white data-[state=active]:text-emerald-600 text-white font-semibold py-3">
                Other
              </TabsTrigger>
            </TabsList>

            <TabsContent value="popular" className="mt-8">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {popularServices.map(service => (
                  <ServiceCard key={service.id} service={service} />
                ))}
              </div>
            </TabsContent>

            <TabsContent value="registrations" className="mt-8">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {registrationServices.map(service => (
                  <ServiceCard key={service.id} service={service} />
                ))}
              </div>
            </TabsContent>

            <TabsContent value="tax" className="mt-8">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {taxServices.map(service => (
                  <ServiceCard key={service.id} service={service} />
                ))}
              </div>
            </TabsContent>

            <TabsContent value="other" className="mt-8">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {otherServices.map(service => (
                  <ServiceCard key={service.id} service={service} />
                ))}
              </div>
            </TabsContent>
          </Tabs>
        </div>
      </section>

      {/* Consulting Solutions callout band */}
      <section className="py-16 bg-gradient-to-br from-emerald-950 via-emerald-900 to-black text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-10">
            <p className="text-sm tracking-widest text-emerald-400 uppercase mb-2">
              Consulting Solutions
            </p>
            <h2 className="text-3xl md:text-4xl font-bold" style={{ fontFamily: "'Rethink Sans', 'Inter', sans-serif", letterSpacing: '-0.02em' }}>
              Beyond compliance — we help you <span className="text-emerald-400">grow</span>.
            </h2>
            <p className="text-emerald-100/80 mt-3 max-w-2xl mx-auto">
              Strategic services for established businesses looking to win bigger tenders, score better on B-BBEE, and reach more customers.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-6">
            {[
              {
                id: 'iso-certification',
                Icon: Award,
                title: 'ISO Certification',
                tag: 'ISO 9001 / 14001 / 45001',
                copy: 'Gap analysis, system implementation and audit support — get tender-ready and operate to global best practice.',
              },
              {
                id: 'bee-equity-solutions',
                Icon: Scale,
                title: 'BEE & Equity Solutions',
                tag: 'Strategy · ESD · EEIP',
                copy: 'Tailored B-BBEE scorecard strategy and Equity Equivalent Investment Programmes for corporates & multinationals.',
              },
              {
                id: 'marketing-solutions',
                Icon: Megaphone,
                title: 'Marketing Solutions',
                tag: 'Brand · Web · Digital',
                copy: 'Brand identity, websites, social media and paid digital campaigns — build presence and generate leads.',
              },
            ].map(({ id, Icon, title, tag, copy }) => (
              <div
                key={id}
                onClick={() => navigate(`/service/${id}`)}
                className="group cursor-pointer p-6 rounded-xl bg-white/5 hover:bg-white/10 border border-emerald-700/40 hover:border-emerald-400 transition-all duration-300 backdrop-blur-sm flex flex-col"
                data-testid={`consulting-card-${id}`}
              >
                <div className="w-12 h-12 rounded-lg bg-emerald-500/20 border border-emerald-400/30 flex items-center justify-center mb-4 group-hover:bg-emerald-500/30 transition-colors">
                  <Icon className="h-6 w-6 text-emerald-300" />
                </div>
                <p className="text-xs uppercase tracking-wider text-emerald-400/80 mb-1">{tag}</p>
                <h3 className="text-xl font-bold text-white mb-2" style={{ fontFamily: "'Rethink Sans', sans-serif" }}>
                  {title}
                </h3>
                <p className="text-sm text-emerald-100/80 mb-5 flex-1">{copy}</p>
                <div className="flex items-center text-emerald-300 group-hover:text-emerald-200 font-semibold text-sm">
                  Request a quote
                  <ArrowRight className="h-4 w-4 ml-1 group-hover:translate-x-1 transition-transform" />
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Call Center & AI Section */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 gap-8">
            <Card className="text-center p-8 hover:shadow-lg transition-shadow">
              <div className="text-red-600 mb-4">
                <svg className="w-16 h-16 mx-auto" fill="currentColor" viewBox="0 0 20 20">
                  <path d="M2 3a1 1 0 011-1h2.153a1 1 0 01.986.836l.74 4.435a1 1 0 01-.54 1.06l-1.548.773a11.037 11.037 0 006.105 6.105l.774-1.548a1 1 0 011.059-.54l4.435.74a1 1 0 01.836.986V17a1 1 0 01-1 1h-2C7.82 18 2 12.18 2 5V3z" />
                </svg>
              </div>
              <h3 className="text-2xl font-bold mb-3">Call Centre</h3>
              <div className="space-y-1" data-testid="home-call-centre">
                <p className="text-2xl font-bold text-emerald-600">012 824 3708</p>
                <p className="text-2xl font-bold text-emerald-600">078 285 0927</p>
                <p className="text-2xl font-bold text-emerald-600">050 057 8315</p>
              </div>
              <p className="text-sm text-gray-500 mt-3">
                WhatsApp: 079 042 4911 · 078 285 0927 · 050 057 8315
              </p>
            </Card>

            <Card className="text-center p-8 hover:shadow-lg transition-shadow bg-gradient-to-br from-emerald-50 to-white">
              <div className="w-16 h-16 bg-emerald-600 text-white rounded-full flex items-center justify-center text-2xl font-bold mx-auto mb-4">
                AI
              </div>
              <h3 className="text-2xl font-bold mb-2">Ask us anything</h3>
              <Button
                onClick={() => navigate('/contact')}
                className="bg-blue-600 hover:bg-blue-700 mt-2"
                data-testid="home-chat-now-btn"
              >
                CHAT NOW
              </Button>
            </Card>
          </div>
        </div>
      </section>

      {/* Latest from Resources */}
      {(() => {
        const latest = [...articles]
          .sort((a, b) => (a.datePublished < b.datePublished ? 1 : -1))
          .slice(0, 3);
        if (!latest.length) return null;
        return (
          <section className="py-16 bg-gray-50" data-testid="home-latest-resources">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
              <div className="flex items-end justify-between mb-8 gap-4 flex-wrap">
                <div>
                  <p className="text-emerald-600 text-sm font-semibold tracking-widest uppercase mb-2">
                    Resources
                  </p>
                  <h2 className="text-3xl md:text-4xl font-bold text-gray-900">
                    Latest from our compliance guides
                  </h2>
                </div>
                <Button
                  variant="outline"
                  onClick={() => navigate('/blog')}
                  className="border-emerald-600 text-emerald-700 hover:bg-emerald-50"
                  data-testid="home-view-all-resources"
                >
                  Browse all guides <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              </div>
              <div className="grid gap-6 md:grid-cols-3">
                {latest.map((a) => (
                  <Link
                    key={a.slug}
                    to={`/blog/${a.slug}`}
                    className="group bg-white rounded-lg border hover:shadow-md transition-shadow overflow-hidden"
                    data-testid={`home-resource-${a.slug}`}
                  >
                    <div className="h-40 overflow-hidden">
                      <img
                        src={`${a.heroImage}${a.heroImage.includes('?') ? '&' : '?'}q=70&auto=format`}
                        alt={a.title}
                        loading="lazy"
                        decoding="async"
                        width="600"
                        height="160"
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      />
                    </div>
                    <div className="p-5">
                      <p className="text-xs text-gray-500 mb-2">{a.readTime}</p>
                      <h3 className="font-bold text-gray-900 leading-snug group-hover:text-emerald-600 text-base mb-2">
                        {a.title}
                      </h3>
                      <p className="text-sm text-gray-600 line-clamp-3">{a.description}</p>
                      <span className="inline-flex items-center gap-1 mt-3 text-emerald-600 text-sm font-semibold group-hover:gap-2 transition-all">
                        Read the guide <ArrowRight className="h-4 w-4" />
                      </span>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          </section>
        );
      })()}

      {/* FAQs */}
      <section id="faqs" className="py-20 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-4xl font-bold text-center mb-12">
            Frequently Asked <span className="text-emerald-600">Questions</span>
          </h2>
          <Accordion type="single" collapsible className="space-y-4">
            {homeFaqItems.map((item, idx) => (
              <AccordionItem
                key={`home-faq-${idx}`}
                value={`home-faq-${idx}`}
                className="border rounded-lg px-6"
              >
                <AccordionTrigger
                  className="text-left font-semibold hover:text-emerald-600"
                  data-testid={`home-faq-q-${idx}`}
                >
                  {item.q}
                </AccordionTrigger>
                <AccordionContent className="text-gray-600">
                  {item.a}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
          <div className="text-center mt-8">
            <Button
              onClick={() => navigate('/faqs')}
              variant="outline"
              className="border-emerald-600 text-emerald-700 hover:bg-emerald-50"
              data-testid="home-view-all-faqs"
            >
              View all FAQs <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
          </div>
        </div>
      </section>

      <ChatBot />
      <Footer />
    </div>
  );
};

export default Home;
